import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faPlus } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createTenant } from "../../api/Tenants";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import EditForm from "../../components/EditForm";
import { useSidebar } from "../../providers/SidebarContext";
import { getLdapCredentials } from "../../api/Tenants";
import Dropdown from "../../components/Dropdown";
import InputField from "../../components/InputField";
import Switch from "../../components/Switch";

const CreateTenantPage = () => {
  const { getAccessToken } = useAuth();
  const { agreementPublicId, user } = useParams();
  const navigate = useNavigate();
  const { syncData } = useSidebar();
  const [hasChanges, setHasChanges] = useState(false);
  const [ldapCredentials, setLdapCredentials] = useState([]);
  const [loadingLdapCredential, setLoadingLdapCredentials] = useState(true);
  const [tenant, setTenant] = useState({
    agreementPublicId: agreementPublicId || "",
  });
  
  useEffect(() => {
    handleGetLdapCredentials([]);
  }, []);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCreateTenant = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }
  
      if (!tenant.agreementPublicId) {
        toast.error("The tenant must be associated with an agreement.");
        return;
      }
  
      // bygger ny
      let tenantData = {
        ...tenant,
        // The input field yields a string; the API expects a number or null
        // (empty field = no Ingram connection).
        ingramCustomerId:
          tenant.ingramCustomerId === "" || tenant.ingramCustomerId == null
            ? null
            : Number(tenant.ingramCustomerId),
        ldapCredentialPublicId: tenant.ldapCredentialPublicId || null,
        ldapUsername: tenant.ldapUsername || "",
        ldapPassword: tenant.ldapPassword || "",
        ldapDomain: tenant.ldapDomain || "",
        isLdapSyncEnabled: tenant.isLdapSyncEnabled ?? false,
        isEntraSyncEnabled: tenant.isEntraSyncEnabled ?? false,
        isEnabled: tenant.isEnabled ?? true,
      };
  
      if (tenant.ldapCredentialPublicId) {
        tenantData.ldapCredentialPublicId = tenant.ldapCredentialPublicId;
      } else {
        tenantData = {
          ...tenantData,
          ldapCredentialPublicId: null,
          ldapUsername: tenant.ldapUsername,
          ldapPassword: tenant.ldapPassword,
          ldapDomain: tenant.ldapDomain,
          isLdapSyncEnabled: tenant.isLdapSyncEnabled || false,
        };
      }
  
      console.log("Sending Tenant Data:", tenantData);
  
      await createTenant(tenantData, accessToken);
  
      toast.success("Tenant created successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
  
      syncData();
      navigate(-1);
    } catch (error) {
      console.error("Failed to create tenant:", error);
      toast.error("Failed to create tenant");
    }
  };

  const handleGetLdapCredentials = async () => {
    try {
      const accessToken = await getAccessToken();
      const ldapCredentials = await getLdapCredentials(accessToken);
      setLdapCredentials(ldapCredentials);
      console.log("LDAP Credentials:", ldapCredentials);
    } catch (error) {
      console.error("Failed to fetch LDAP credentials:", error);
    } finally {
      setLoadingLdapCredentials(false);
    }
  };

const isLdapReadOnly = !!tenant?.ldapCredentialPublicId;

const ldapCredentialOptions = [
  ...ldapCredentials.map((cred) => cred.ldapUsername),
  "New LDAP"
];

const selectedLdapValue =
tenant?.ldapCredentialPublicId
  ? ldapCredentials.find(c => c.ldapCredentialPublicId === tenant.ldapCredentialPublicId)?.ldapUsername
  : "New LDAP";

const handleLdapCredentialSelect = (selectedOption) => {
  if (selectedOption === "New LDAP") {
    setTenant((prevData) => ({
      ...prevData,
      ldapCredential: "New LDAP",
      ldapCredentialPublicId: null,
      ldapUsername: "",
      ldapPassword: "",
    }));
  }else {
    const selectedCredential = ldapCredentials.find((cred) => cred.ldapUsername === selectedOption);
    if (selectedCredential) {
      setTenant((prevData) => ({
        ...prevData,
        ldapCredentialPublicId: selectedCredential.ldapCredentialPublicId,
        ldapUsername: selectedCredential.ldapUsername,
        ldapPassword: selectedCredential.ldapPassword,
      }));
    }
  }}
console.log("CREATE TENANT PAGE");

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Create Tenant</h2>
              <div className="flex gap-4 ml-auto">
                <button className="btn btn-outline-primary" onClick={handleBackClick}>
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={!hasChanges} onClick={handleCreateTenant}>
                Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={tenant}
                setData={setTenant}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  { key: "agreementPublicId", label: "Agreement ID", type: "text", disabled: true}, 
                  { key: "entraTenantId", label: "Entra ID", type: "text" },
                  { key: "ingramCustomerId", label: "Ingram ID", type: "number" },
                  { key: "displayName", label: "Name", type: "text" },
                  { key: "domain", label: "Entra domain", type: "text" },               
                  { key: "isEntraSyncEnabled", label: "Entra sync", type: "switch" },
                  { key: "isEnabled", label: "Enabled", type: "switch" },
                ]}
              />
               <div className="flex flex-col border-t">
                <div className="flex flex-row items-center py-4">
                  <h2 className="">LDAP settings</h2>
                </div>
                <div className="grid grid-cols-2 gap-6 w-full">
                  
                  <Dropdown
                    label="LDAP credential"
                    value={[selectedLdapValue]}
                    options={ldapCredentialOptions}
                    onSelect={handleLdapCredentialSelect}
                    disabled={false}
                  />

                  <InputField
                    label="LDAP domain"
                    value={tenant?.ldapDomain || ""}
                    onChange={(e) => {
                      setTenant((prevData) => ({
                        ...prevData,
                        ldapDomain: e.target.value
                      }))
                      setHasChanges(true)
                    }}
                    disabled={false}
                    type="text"
                  />

                  <InputField
                    label="LDAP username"
                    value={tenant?.ldapUsername || ""}
                    onChange={(e) => {
                      setTenant((prevData) => ({
                        ...prevData,
                        ldapUsername: e.target.value
                      }))
                      setHasChanges(true)
                    }}
                    disabled={isLdapReadOnly}
                    type="text"
                  />

                  <InputField
                    label="LDAP password"
                    value={tenant?.ldapPassword || ""}
                    onChange={(e) => {
                      setTenant((prevData) => ({
                        ...prevData,
                        ldapPassword: e.target.value
                      }))
                      setHasChanges(true)
                    }}
                    disabled={isLdapReadOnly}
                    type="password"
                  />

                  <div className="flex flex-col w-1/2">
                    <label className="text-sm font-bold pb-2">LDAP sync</label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={tenant?.isLdapSyncEnabled || false}
                        onChange={(checked) => {
                          setTenant((prevData) => ({
                            ...prevData,
                            isLdapSyncEnabled: checked,
                          }))
                          setHasChanges(true)
                        }}
                        disabled={false}
                      />
                      <p className="text-sm">
                        {tenant?.isLdapSyncEnabled ? "Enabled" : "Disabled"}
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateTenantPage;